import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import ReactQuill from 'react-quill-new';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Button } from '@mui/material';

import { selectSortedCommunicationTemplates } from '../../../../store/communicationSelectors';
import {
    clearCommunicationTemplates,
    clearCurrentTemplate,
    clearPreviewCommunication,
    loadCommunicationTemplate,
    previewParticipantLetterCommunication,
    searchCommunicationTemplates
} from '../../../../store/communicationService';
import { loadCommunicationTypes, loadTimelineTypeDetails } from '../../../../store/directusService';
import {
    createParticipantTimelineCommunicationEvent,
    loadServiceConstants
} from '../../../../store/participantService';
import { getCurrentTime, reverseFormatDate } from '../../../../utils/dateFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import CommunicationsErrorModal from '../../../communication/CommunicationsErrorModal';
import IconError from '../../../IconError';
import DDLOptionPicker from '../../../ui/pickers/DDLOptionPicker';

import { initialErrorState, validate } from './validateCommunications';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const LetterCommunication = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
    const initialState = {
        date: reverseFormatDate(new Date().toISOString().slice(0, 10)),
        templateId: '',
        templateName: '',
        details: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [isPdfGenerated, setIsPdfGenerated] = useState(false);
    const [fileTitle, setFileTitle] = useState('');
    const [isSucceeded, setIsSucceeded] = useState(false);
    const [timelineTypeId, setTimelineTypeId] = useState(false);
    const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(false);
    const [placeholderErrorOpen, setPlaceholderErrorOpen] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const communicationTypes = useSelector(
        (state) => state.entities.directusService.communicationTypes
    );
    const serviceConstants = useSelector(
        (state) => state.entities.participantService.serviceConstants
    );
    const timelineTypeDetails = useSelector(
        (state) => state.entities.directusService.timelineTypeDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const templates = useSelector(selectSortedCommunicationTemplates);
    const currentTemplate = useSelector(
        (state) => state.entities.communicationService.communicationTemplate
    );
    const communicationPreview = useSelector(
        (state) => state.entities.communicationService.communicationPreview
    );
    const communicationPreviewLoading = useSelector(
        (state) => state.entities.communicationService.communicationPreviewLoading
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (communicationTypes?.length < 1) dispatch(loadCommunicationTypes());
        if (timelineTypeDetails?.length < 1) dispatch(loadTimelineTypeDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!currentParticipant || Object.keys(currentParticipant).length < 1) return;
        dispatch(
            searchCommunicationTemplates({
                type: 'Letter',
                serviceIds: [currentParticipant.serviceId],
                inactive: false
            })
        );
        if (!serviceConstants[currentParticipant.serviceId])
            dispatch(loadServiceConstants(currentParticipant.serviceId));
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !newEntry.templateId ||
            Object.keys(currentTemplate).length < 1 ||
            communicationTypes?.length < 1
        )
            return;
        if (communicationTypes.find((el) => el.id === currentTemplate.typeId)?.name !== 'Letter')
            return;
        setNewEntry((prev) => ({
            ...prev,
            details: currentTemplate.details
        }));
    }, [newEntry.templateId, currentTemplate, communicationTypes]);

    useEffect(() => {
        if (templates?.length < 1 || !state) return;
        const constants = serviceConstants[currentParticipant.serviceId];
        if (constants) {
            let foundTemplate;
            if (
                state.status?.toLowerCase() === 'not known' &&
                constants?.notKnownLetterTemplateId
            ) {
                foundTemplate = templates.find(
                    (el) => el.id === constants?.notKnownLetterTemplateId
                );
            } else if (
                state.status?.toLowerCase() === 'failed to attend' &&
                constants?.ftaLetterTemplateId
            ) {
                foundTemplate = templates.find((el) => el.id === constants?.ftaLetterTemplateId);
            }
            if (foundTemplate) {
                dispatch(loadCommunicationTemplate(foundTemplate.id));
                setNewEntry((prev) => ({
                    ...prev,
                    templateId: foundTemplate.id,
                    templateName: foundTemplate.name
                }));
            }
        }
    }, [serviceConstants, templates]);

    useEffect(() => {
        setTimelineTypeId(
            timelineTypeDetails.find((el) => el.name === 'Letter Generated')?.id || ''
        );
    }, [timelineTypeDetails]);

    useEffect(() => {
        if (
            !newEntry.templateId ||
            Object.keys(currentTemplate).length < 1 ||
            communicationTypes?.length < 1
        )
            return;
        if (communicationTypes.find((el) => el.id === currentTemplate.typeId)?.name !== 'Letter')
            return;

        if (currentTemplate.details && currentTemplate.details.includes('{{')) {
            dispatch(
                previewParticipantLetterCommunication(
                    {
                        participantId: currentParticipant.id,
                        body: currentTemplate.details,
                        ...(state?.appointmentId && { appointmentId: state.appointmentId })
                    },
                    currentParticipant.id
                )
            );
        } else
            setNewEntry((prev) => ({
                ...prev,
                details: currentTemplate.details
            }));
    }, [newEntry.templateId, currentTemplate, communicationTypes]);

    useEffect(() => {
        if (communicationPreview.body && !placeholderErrorOpen)
            setNewEntry((prev) => ({
                ...prev,
                details: communicationPreview.body
            }));
    }, [communicationPreview]);

    useEffect(() => {
        if (isPdfGenerated) {
            setIsSucceeded(true);
            createParticipantTimelineCommunicationEntry();
        }
    }, [isPdfGenerated]);

    useEffect(() => {
        if (successMessage === 'Timeline communication entry has been added') {
            setIsSendButtonDisabled(false);
            clearForm();
            const timeoutId = setTimeout(() => setIsSucceeded(false), 6000);
            return () => clearTimeout(timeoutId);
        }
    }, [successMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setErrors(initialErrorState);
        setIsPdfGenerated(false);
        dispatch(clearCurrentTemplate());
    };

    const cleanUp = () => {
        clearForm();
        dispatch(clearCurrentTemplate());
        dispatch(clearPreviewCommunication());
        dispatch(clearCommunicationTemplates());
    };

    // EVENT HANDLERS
    const onGeneratePdf = () => {
        const fileName = `${newEntry.templateName || 'Letter'} - ${format(
            new Date(),
            'dd/MM/yyyy'
        )} ${getCurrentTime()}`;

        setFileTitle(fileName);
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.html(newEntry.details, {
            async callback(doc) {
                doc.save(fileName);
                setIsPdfGenerated(true);
            },
            autoPaging: 'text',
            margin: [10, 5, 10, 5],
            width: 210,
            windowWidth: 1050
        });
    };

    const createParticipantTimelineCommunicationEntry = () => {
        const currentDate = format(new Date(), 'yyyy-MM-dd');
        const currentTime = new Date().toLocaleTimeString().slice(0, 5).replaceAll(':', '');

        let paddedTime;
        currentTime.length === 3 ? (paddedTime = `0${currentTime}`) : (paddedTime = currentTime);

        const payload = {
            participantId: currentParticipant.id,
            eventStatusId: '00000000-0000-0000-0000-000000000000',
            eventDate: currentDate,
            itemTypeId: timelineTypeId,
            eventTime: paddedTime,
            eventType: 'Outbound Communication',
            loggedDate: currentDate,
            loggedTime: paddedTime,
            userId: loggedInUser?.id,
            subject: newEntry.templateName || 'Ad Hoc Letter',
            details: newEntry.details.replaceAll('&nbsp;', ' '),
            templateId: newEntry.templateId || null
        };
        dispatch(createParticipantTimelineCommunicationEvent(payload));
    };

    const clearError = (key) => {
        setErrors((prev) => ({
            ...prev,
            [key]: { error: false, message: '' }
        }));
    };

    const onDetailsChange = (details) => {
        clearError('details');
        setNewEntry((prev) => ({ ...prev, details }));
    };

    const onProcessedPlaceholders = (newMessage) => {
        setNewEntry((prev) => ({
            ...prev,
            details: newMessage
        }));
    };

    const onPreviewClick = () => {
        const { isValid, newErrors } = validate(newEntry, errors, 'Letter');
        setErrors(newErrors);
        if (!isValid) return;
        if (newEntry.details && newEntry.details.includes('{{')) setPlaceholderErrorOpen(true);
        else onSubmit();
    };

    const onSubmit = () => {
        setIsSendButtonDisabled(true);
        onGeneratePdf();
    };

    return (
        <div className={app.pageContainer} id="letterScreen">
            <CommunicationsErrorModal
                isOpen={placeholderErrorOpen}
                participants={[currentParticipant]}
                payload={{
                    body: newEntry.details,
                    participantId: currentParticipant.id,
                    ...(state?.appointmentId && { appointmentId: state.appointmentId })
                }}
                previewCommunication={previewParticipantLetterCommunication}
                onAccept={onSubmit}
                onClose={() => setPlaceholderErrorOpen(false)}
                onMessageProcessed={onProcessedPlaceholders}
            />
            <h1 className={app.mainHeading}>Communications</h1>
            <form
                className={form.formWrapper}
                onSubmit={(e) => {
                    e.preventDefault();
                    onPreviewClick();
                }}>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={form.textAreaSet}>
                            <label>Date</label>
                            <div className={form.textAreaText}>{newEntry.date}</div>
                        </div>
                    </div>
                    <div className={form.formColumn}>
                        <div className={form.alignDownMed} id="letterTemplateInput">
                            <DDLOptionPicker
                                label={'Template'}
                                id={'templates'}
                                disabled={!hasRole(acceptedRoles, roles)}
                                menuItems={templates || []}
                                chosenName={newEntry.templateName}
                                chosenId={newEntry.templateId}
                                onChange={(chosenId) => {
                                    chosenId && dispatch(loadCommunicationTemplate(chosenId));
                                    const chosenTemplate = templates?.find(
                                        (el) => el.id === chosenId
                                    );
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        templateId: chosenId || '',
                                        templateName: chosenTemplate?.name || ''
                                    }));
                                }}></DDLOptionPicker>
                        </div>
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={form.textAreaSet}>
                            <ReactQuill
                                value={newEntry.details}
                                modules={LetterCommunication.modules}
                                placeholder="Enter details"
                                onChange={onDetailsChange}
                            />
                            {errors.details.error && (
                                <div className={`${form.textInputError} ${form.resetMargin}`}>
                                    <IconError text={errors.details} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={form.buttonRight}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={
                            !hasRole(acceptedRoles, roles) ||
                            newEntry.details.length < 1 ||
                            isSendButtonDisabled ||
                            Object.values(errors).some((el) => el.error) ||
                            communicationPreviewLoading
                        }>
                        Send Now
                    </Button>
                </div>
                {isSucceeded && (
                    <div className={form.successMessage}>PDF {fileTitle} downloaded</div>
                )}
            </form>
        </div>
    );
};

export default LetterCommunication;

LetterCommunication.modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['clean']
    ]
};
